/**
 * Computes area-comparison decile buckets for all products.
 *
 * Groups products by rooms count, sorts each group by area_original, and
 * assigns each product a decile label 'p0'–'p9' within its group:
 *   p0 = bottom 10%  (smallest apartments for that rooms count)
 *   p9 = top   10%   (largest apartments for that rooms count)
 *
 * Boundary logic — same algorithm as getCompareDecile in svgColorScheme.js,
 * but precomputed per product UUID at product (not compartment) granularity:
 *   boundary[i] = sorted[ floor(n × (i+1) / 10) ].area_original  (i = 0..8)
 *   product gets bucket 'p{i}' if area < boundary[i]; falls through to 'p9'.
 *
 * NOTE: This is intentionally separate from buildCompareDeciles (svgColorScheme.js),
 * which groups by compartment name_cad for InfoMode compartment-level coloring.
 * Different granularity, different index key — do not merge.
 *
 * Expensive — iterates all products twice (group + sort + scan).
 * Call once after products load and cache the result (e.g. in a React useMemo).
 *
 * @param {Array} products — array of product objects with uuid, rooms, area_original
 * @returns {Map<string, 'p0'|'p1'|'p2'|'p3'|'p4'|'p5'|'p6'|'p7'|'p8'|'p9'>}
 *          Maps product uuid → decile bucket
 */
export function buildProductAreaDeciles(products) {
  // Group by rooms count (products with no area_original are skipped — no meaningful bucket)
  const byRooms = new Map()
  for (const p of products) {
    if (p.uuid == null || p.area_original == null) continue
    const rooms = p.rooms ?? 0
    if (!byRooms.has(rooms)) byRooms.set(rooms, [])
    byRooms.get(rooms).push(p)
  }

  const result = new Map()

  for (const group of byRooms.values()) {
    const sorted = group.slice().sort((a, b) => a.area_original - b.area_original)
    const n = sorted.length

    // Precompute the 9 boundary values at 10th, 20th, …, 90th percentile positions
    const boundaries = Array.from(
      { length: 9 },
      (_, i) => sorted[Math.floor(n * (i + 1) / 10)].area_original,
    )

    for (const p of group) {
      let bucket = 9
      for (let i = 0; i < 9; i++) {
        if (p.area_original < boundaries[i]) {
          bucket = i
          break
        }
      }
      result.set(p.uuid, `p${bucket}`)
    }
  }

  return result
}
